import { Fragment, useEffect, useMemo, useState } from "react";
import { Download, Filter, RefreshCw, Printer, X, ChevronDown, ChevronRight } from "lucide-react";
import api from "../../api/client";
import { formatPrice, formatNumber } from "../../lib/format";
import SnapshotTimeline from "./SnapshotTimeline";

export default function ClosedPositionsPanel() {
    const [filters, setFilters] = useState({
        coin: "", from_date: "", to_date: "",
        min_notional: "", max_notional: "",
        min_margin: "", max_margin: "",
        outcome: "", exit_type: "",
    });
    const [data, setData] = useState(null);
    const [busy, setBusy] = useState(false);
    const [downloading, setDownloading] = useState(false);
    const [expandedId, setExpandedId] = useState(null);

    const params = useMemo(() => {
        const qs = new URLSearchParams();
        Object.entries(filters).forEach(([k, v]) => {
            if (v !== "" && v !== null && v !== undefined) qs.set(k, v);
        });
        qs.set("limit", "500");
        return qs.toString();
    }, [filters]);

    const load = async () => {
        setBusy(true);
        try {
            const r = await api.get(`/swaps/closed?${params}`);
            setData(r.data);
        } catch { /* swallow */ } finally {
            setBusy(false);
        }
    };
    useEffect(() => { load(); /* eslint-disable-next-line */ }, [params]);

    const reset = () => setFilters({
        coin: "", from_date: "", to_date: "",
        min_notional: "", max_notional: "",
        min_margin: "", max_margin: "",
        outcome: "", exit_type: "",
    });

    const downloadCsv = async () => {
        setDownloading(true);
        try {
            const r = await api.get(`/swaps/closed.csv?${params}`, { responseType: "blob" });
            const link = document.createElement("a");
            link.href = URL.createObjectURL(new Blob([r.data], { type: "text/csv" }));
            const today = new Date().toISOString().slice(0, 10);
            link.download = `verslan-positions-${today}.csv`;
            link.click();
            URL.revokeObjectURL(link.href);
        } catch { /* swallow */ } finally {
            setDownloading(false);
        }
    };

    const printPdf = () => window.print();

    const set = (k, v) => setFilters({ ...filters, [k]: v });

    const items = data?.items || [];
    const totals = useMemo(() => {
        let pnl = 0, fees = 0, refund = 0, notional = 0;
        for (const s of items) {
            // user_side determines whether pnl was a gain or loss for me
            const won = (s.user_side === "BULL" && s.outcome === "BULLISH")
                     || (s.user_side === "BEAR" && s.outcome === "BEARISH");
            if (s.outcome === "NEUTRAL") pnl += 0;
            else pnl += won ? (s.pnl || 0) : -(s.pnl || 0);
            fees += (s.fee_per_side || 0) + (s.liquidation_fee || 0);
            refund += s.loser_refund || 0;
            notional += s.notional || 0;
        }
        return { pnl, fees, refund, notional, count: items.length };
    }, [items]);

    return (
        <div className="bg-[#0A0A0C] border border-[#1F1F24] rounded-sm" data-testid="closed-positions-panel">
            <div className="px-4 py-3 border-b border-[#1F1F24] flex items-baseline justify-between flex-wrap gap-2">
                <div>
                    <div className="text-[10px] font-mono uppercase tracking-[0.22em] text-zinc-500">Closed positions · Trade history</div>
                    <div className="text-sm font-medium font-mono tracking-tight mt-0.5">
                        {data ? `${totals.count} trade${totals.count === 1 ? "" : "s"}` : "loading…"}
                        {" · "}
                        <span className="text-zinc-400">
                            net P&amp;L <span className={totals.pnl >= 0 ? "text-emerald-400" : "text-rose-400"}>
                                {totals.pnl >= 0 ? "+" : ""}${formatNumber(totals.pnl)}
                            </span>
                            {" · "}
                            fees ${formatNumber(totals.fees)}
                            {totals.refund > 0 && <> · refunds <span className="text-cyan-300">${formatNumber(totals.refund)}</span></>}
                            {" · "}
                            volume ${formatNumber(totals.notional)}
                        </span>
                    </div>
                </div>
                <div className="flex items-center gap-2">
                    <button onClick={load} disabled={busy} data-testid="closed-refresh"
                            className="flex items-center gap-1 text-[10px] font-mono uppercase tracking-widest text-zinc-400 hover:text-cyan-300 disabled:opacity-40">
                        <RefreshCw className={`w-3 h-3 ${busy ? "animate-spin" : ""}`} /> Refresh
                    </button>
                    <button onClick={printPdf} data-testid="closed-print"
                            className="flex items-center gap-1 px-3 py-1.5 border border-[#1F1F24] hover:border-cyan-400 text-zinc-300 hover:text-cyan-300 text-[10px] font-mono uppercase tracking-widest rounded-sm">
                        <Printer className="w-3 h-3" /> Print / PDF
                    </button>
                    <button onClick={downloadCsv} disabled={downloading} data-testid="closed-csv-download"
                            className="flex items-center gap-1 px-3 py-1.5 border border-cyan-500/30 hover:border-cyan-400 text-cyan-300 text-[10px] font-mono uppercase tracking-widest rounded-sm disabled:opacity-40">
                        <Download className={`w-3 h-3 ${downloading ? "animate-pulse" : ""}`} /> {downloading ? "…" : "Download CSV"}
                    </button>
                </div>
            </div>

            <div className="px-4 py-3 grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-2 border-b border-[#1F1F24] bg-[#050507]">
                <FilterField label="From" type="date" value={filters.from_date} onChange={(v) => set("from_date", v)} testid="filter-from-date" />
                <FilterField label="To"   type="date" value={filters.to_date}   onChange={(v) => set("to_date", v)} testid="filter-to-date" />
                <FilterField label="Coin" placeholder="e.g. HYPE" value={filters.coin} onChange={(v) => set("coin", v.toUpperCase())} testid="filter-coin" />
                <FilterField label="Outcome" select value={filters.outcome} onChange={(v) => set("outcome", v)} testid="filter-outcome"
                             options={["", "BULLISH", "BEARISH", "NEUTRAL"]} />
                <FilterField label="Exit type" select value={filters.exit_type} onChange={(v) => set("exit_type", v)} testid="filter-exit-type"
                             options={["", "EXPIRY", "LIQUIDATION", "EARLY_EXIT"]} />
                <FilterField label="Min notional" type="number" value={filters.min_notional} onChange={(v) => set("min_notional", v)} testid="filter-min-notional" />
                <FilterField label="Max notional" type="number" value={filters.max_notional} onChange={(v) => set("max_notional", v)} testid="filter-max-notional" />
                <FilterField label="Min margin" type="number" value={filters.min_margin} onChange={(v) => set("min_margin", v)} testid="filter-min-margin" />
                <FilterField label="Max margin" type="number" value={filters.max_margin} onChange={(v) => set("max_margin", v)} testid="filter-max-margin" />
                <div className="self-end">
                    <button onClick={reset} data-testid="filter-reset"
                            className="w-full flex items-center justify-center gap-1 px-2 py-1.5 border border-[#1F1F24] hover:border-rose-400 text-zinc-400 hover:text-rose-300 text-[10px] font-mono uppercase tracking-widest rounded-sm">
                        <X className="w-3 h-3" /> Reset filters
                    </button>
                </div>
            </div>

            <div className="overflow-x-auto">
                <table className="w-full text-[12px] font-mono">
                    <thead>
                        <tr className="text-[10px] uppercase tracking-[0.22em] text-zinc-500 border-b border-[#1F1F24]">
                            <th className="text-left py-2 px-4">Settled (UTC)</th>
                            <th className="text-left py-2 px-4">Pair · Duration</th>
                            <th className="text-left py-2 px-4">Side</th>
                            <th className="text-right py-2 px-4">Notional</th>
                            <th className="text-right py-2 px-4">Margin</th>
                            <th className="text-right py-2 px-4">Entry → Exit</th>
                            <th className="text-left py-2 px-4">Result</th>
                            <th className="text-right py-2 px-4">P&amp;L (net)</th>
                            <th className="text-right py-2 px-4">Fees</th>
                            <th className="text-right py-2 px-4">Refund</th>
                        </tr>
                    </thead>
                    <tbody>
                        {items.map((s) => {
                            const won = (s.user_side === "BULL" && s.outcome === "BULLISH")
                                     || (s.user_side === "BEAR" && s.outcome === "BEARISH");
                            const pnl = s.outcome === "NEUTRAL" ? 0 : (won ? s.pnl : -s.pnl);
                            const fees = (s.fee_per_side || 0) + (s.liquidation_fee || 0);
                            const pair = `${s.coin}-${s.cross_coin || "USD"}`;
                            const isOpen = expandedId === s.id;
                            return (
                                <Fragment key={s.id}>
                                <tr
                                    className="border-b border-[#1F1F24]/60 hover:bg-cyan-500/[0.04] cursor-pointer"
                                    onClick={() => setExpandedId(isOpen ? null : s.id)}
                                    data-testid={`closed-row-${s.id}`}
                                >
                                    <td className="py-2 px-4 text-zinc-400 whitespace-nowrap">
                                        <span className="inline-flex items-center gap-1">
                                            {isOpen ? <ChevronDown className="w-3 h-3 text-cyan-300" /> : <ChevronRight className="w-3 h-3 text-zinc-600" />}
                                            {(s.settled_at || "").replace("T", " ").slice(0, 19)}
                                        </span>
                                    </td>
                                    <td className="py-2 px-4 text-zinc-200 whitespace-nowrap">
                                        {pair} <span className="text-zinc-500">· {s.duration_key}</span>
                                        {s.via_bot && <div className="text-[9px] text-zinc-600">via {s.via_bot}</div>}
                                    </td>
                                    <td className={`py-2 px-4 ${s.user_side === "BULL" ? "text-emerald-300" : "text-rose-300"}`}>{s.user_side}</td>
                                    <td className="py-2 px-4 text-right tabular-nums">${formatNumber(s.notional)}</td>
                                    <td className="py-2 px-4 text-right tabular-nums">${formatNumber(s.user_margin)}</td>
                                    <td className="py-2 px-4 text-right tabular-nums whitespace-nowrap text-zinc-300">
                                        {formatPrice(s.entry_price)} → {formatPrice(s.exit_price)}
                                    </td>
                                    <td className="py-2 px-4 whitespace-nowrap">
                                        <OutcomePill outcome={s.outcome} exitType={s.exit_type} />
                                    </td>
                                    <td className={`py-2 px-4 text-right tabular-nums ${pnl >= 0 ? "text-emerald-400" : "text-rose-400"}`}>
                                        {pnl >= 0 ? "+" : ""}${formatNumber(pnl)}
                                    </td>
                                    <td className="py-2 px-4 text-right tabular-nums text-zinc-400">${formatNumber(fees)}</td>
                                    <td className="py-2 px-4 text-right tabular-nums text-cyan-300">
                                        {s.loser_refund > 0 ? `$${formatNumber(s.loser_refund)}` : "—"}
                                    </td>
                                </tr>
                                {isOpen && (
                                    <tr className="border-b border-cyan-500/20 bg-[#050507]" data-testid={`closed-row-expanded-${s.id}`}>
                                        <td colSpan={10} className="p-4">
                                            <SnapshotTimeline swap={s} userId={null} />
                                        </td>
                                    </tr>
                                )}
                                </Fragment>
                            );
                        })}
                        {items.length === 0 && (
                            <tr><td colSpan={10} className="text-center py-10 text-zinc-600">No closed trades match these filters.</td></tr>
                        )}
                    </tbody>
                </table>
            </div>
            <div className="px-4 py-2 text-[10px] font-mono text-zinc-600 leading-relaxed border-t border-[#1F1F24]">
                <Filter className="w-3 h-3 inline-block mr-1 -mt-0.5" />
                P&amp;L sign reflects YOUR side (won = +PnL, lost = -PnL). Refund column shows leftover margin credited after liquidation fees.
            </div>
        </div>
    );
}

function FilterField({ label, value, onChange, type = "text", placeholder, select, options, testid }) {
    return (
        <label className="grid gap-1">
            <span className="text-[10px] font-mono uppercase tracking-[0.18em] text-zinc-500">{label}</span>
            {select ? (
                <select data-testid={testid} value={value} onChange={(e) => onChange(e.target.value)}
                        className="bg-[#0A0A0C] border border-[#1F1F24] focus:border-cyan-400 rounded-sm py-1.5 px-2 font-mono text-xs text-zinc-100 outline-none">
                    {options.map((o) => <option key={o} value={o}>{o || "any"}</option>)}
                </select>
            ) : (
                <input data-testid={testid} type={type} value={value} placeholder={placeholder || ""}
                       onChange={(e) => onChange(e.target.value)}
                       className="bg-[#0A0A0C] border border-[#1F1F24] focus:border-cyan-400 rounded-sm py-1.5 px-2 font-mono text-xs text-zinc-100 outline-none tabular-nums" />
            )}
        </label>
    );
}

function OutcomePill({ outcome, exitType }) {
    const theme = {
        BULLISH:  "border-emerald-500/40 bg-emerald-500/5 text-emerald-300",
        BEARISH:  "border-rose-500/40 bg-rose-500/5 text-rose-300",
        NEUTRAL:  "border-zinc-700 text-zinc-400",
    }[outcome] || "border-zinc-700 text-zinc-400";
    return (
        <div className="grid gap-0.5">
            <span className={`inline-block text-[9px] font-mono uppercase tracking-[0.18em] px-1.5 py-0.5 rounded-sm border ${theme}`}>
                {outcome}
            </span>
            {exitType && exitType !== "EXPIRY" && (
                <span className="text-[9px] font-mono uppercase tracking-[0.18em] text-amber-400">{exitType}</span>
            )}
        </div>
    );
}
